'use client';

import { integrationSetupReturnPath } from '@/lib/integrations/setup-return';
import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { useConnectorDraft } from './useConnectorDraft';
import { IntegrationSetupError } from './IntegrationSetupError';

import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import {
  AlertCircle,
  BarChart3,
  Check,
  ExternalLink,
  FilePenLine,
  Globe2,
  RefreshCw,
  ShieldCheck,
  Unlink,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { isAttuneDesktopShell } from '@/lib/desktop-auth-client';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { AttuneSelect } from '@/components/shared/ui';
import { useProject } from '@/contexts/ProjectContext';
import { isDeliveryApprovalsEnabledClient } from '@/lib/delivery-approvals/feature-flags';

interface WordPressSite {
  id?: string;
  siteId?: string;
  site_id?: string;
  name?: string | null;
  title?: string | null;
  url?: string | null;
  type?: string | null;
  isJetpack?: boolean;
  is_jetpack?: boolean;
  writeReady?: boolean;
  write_ready?: boolean;
  siteType?: string | null;
  status?: string | null;
  mcpEnabled?: boolean;
  canRead?: boolean;
  canWrite?: boolean;
  statsAvailable?: boolean;
  lastVerifiedAt?: string | null;
  lastInventoryAt?: string | null;
}

interface WordPressCapabilitySummary {
  readContent?: boolean;
  read_content?: boolean;
  writeContent?: boolean;
  write_content?: boolean;
  statistics?: boolean;
  stats?: boolean;
  disabledReasons?: string[] | null;
  disabled_reasons?: string[] | null;
}

interface WordPressConnectionSummary {
  id?: string;
  status?: string | null;
  lastVerifiedAt?: string | null;
  last_verified_at?: string | null;
  warning?: string | null;
  warnings?: string[] | null;
  verifiedAt?: string | null;
  lastSyncAt?: string | null;
  statusDetail?: string | null;
}

interface WordPressStatus {
  connected?: boolean;
  connection?: WordPressConnectionSummary | null;
  primarySite?: WordPressSite | null;
  primary_site?: WordPressSite | null;
  siteBinding?: WordPressSite | null;
  site_binding?: WordPressSite | null;
  binding?: WordPressSite | null;
  sites?: WordPressSite[] | null;
  capabilities?: WordPressCapabilitySummary | null;
  permissions?: WordPressCapabilitySummary | null;
  statsAvailable?: boolean;
  stats_available?: boolean;
  warning?: string | null;
  needsAttention?: boolean;
  counts?: { mappings?: number; jobsPending?: number } | null;
}

const EMPTY_STATUS: WordPressStatus = {
  connected: false,
  connection: null,
  primarySite: null,
  sites: [],
  capabilities: null,
};

function pickString(...values: Array<string | null | undefined>): string | null {
  return values.find(value => typeof value === 'string' && value.trim())?.trim() || null;
}

function siteId(site: WordPressSite | null | undefined): string {
  return pickString(site?.id, site?.siteId, site?.site_id) || '';
}

function siteTitle(site: WordPressSite | null | undefined): string {
  return pickString(site?.title, site?.name, site?.url) || 'WordPress.com site';
}

function lastVerifiedLabel(value: string | null | undefined): string {
  if (!value) return 'Not verified yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not verified yet';
  return `Verified ${parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

async function readPayload(response: Response): Promise<Record<string, unknown>> {
  return (await response.json().catch(() => ({}))) as Record<string, unknown>;
}

export function WordPressIntegration() {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const setup = useIntegrationSetupFlow(currentProject?.id);
  const projectId = currentProject?.id || null;
  const projectIdRef = useRef(projectId);
  const [status, setStatus] = useState<WordPressStatus>(EMPTY_STATUS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'connect' | 'sites' | 'bind' | 'content' | 'disconnect' | null>(
    null
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [availableSites, setAvailableSites] = useState<WordPressSite[]>([]);
  const deliveryApprovalsEnabled = isDeliveryApprovalsEnabledClient();

  useEffect(() => {
    projectIdRef.current = projectId;
    setAvailableSites([]);
    setSelectedSiteId('');
  }, [projectId]);

  const clearConnectionDraft = useConnectorDraft(
    user?.uid,
    projectId,
    'wordpress',
    { selectedSiteId },
    draft => setSelectedSiteId(draft.selectedSiteId || '')
  );

  const fetchStatus = useCallback(
    async (quiet = false) => {
      if (!user || !projectId) {
        setStatus(EMPTY_STATUS);
        setLoading(false);
        return;
      }
      if (!quiet) setLoading(true);
      setLoadError(null);
      const requestedProjectId = projectId;
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `/api/integrations/wordpress/status?projectId=${encodeURIComponent(requestedProjectId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const payload = (await readPayload(response)) as WordPressStatus & { error?: string };
        if (!response.ok) throw new Error(payload.error || 'Unable to load WordPress.com status');
        if (projectIdRef.current !== requestedProjectId) return;
        setStatus({ ...EMPTY_STATUS, ...payload });
        const binding =
          payload.binding ||
          payload.primarySite ||
          payload.primary_site ||
          payload.siteBinding ||
          payload.site_binding;
        if (Array.isArray(payload.sites) && payload.sites.length) {
          setAvailableSites(payload.sites);
        } else if (binding) {
          setAvailableSites(current =>
            current.some(site => siteId(site) === siteId(binding)) ? current : [binding, ...current]
          );
        }
        setSelectedSiteId(current => siteId(binding) || current || siteId(payload.sites?.[0]));
      } catch (error) {
        if (projectIdRef.current !== requestedProjectId) return;
        const message =
          error instanceof Error ? error.message : 'Unable to load WordPress.com status';
        setLoadError(message);
        setStatus(EMPTY_STATUS);
      } finally {
        if (projectIdRef.current === requestedProjectId) setLoading(false);
      }
    },
    [projectId, user]
  );

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success =
      params.get('wordpress_success') ||
      params.get('wordpress_connected') ||
      params.get('custom_mcp_success');
    const error = params.get('wordpress_error') || params.get('custom_mcp_error');
    if (!success && !error) return;
    if (success === '1' || success === 'true') toast.success('WordPress.com connected.');
    if (error) setup.reportError('WordPress.com connection failed. Please try again.');
    for (const key of [
      'wordpress_success',
      'wordpress_connected',
      'wordpress_error',
      'custom_mcp_success',
      'custom_mcp_error',
      'connectionId',
    ]) {
      params.delete(key);
    }
    const next = params.toString();
    window.history.replaceState(
      {},
      '',
      next ? `${window.location.pathname}?${next}` : window.location.pathname
    );
    void fetchStatus(true);
  }, [fetchStatus]);

  const invoke = useCallback(
    async (
      action:
        | 'start'
        | 'reconnect'
        | 'sites/refresh'
        | 'site-binding'
        | 'content/refresh'
        | 'disconnect',
      body: Record<string, unknown> = {}
    ) => {
      if (!user || !projectId) throw new Error('Select a project first.');
      const token = await user.getIdToken();
      const response = await fetch(`/api/integrations/wordpress/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId, ...body }),
      });
      const payload = await readPayload(response);
      if (!response.ok) {
        throw new Error(
          typeof payload.error === 'string' ? payload.error : 'WordPress action failed'
        );
      }
      return payload;
    },
    [projectId, user]
  );

  const handleConnect = async (reconnect = false) => {
    setup.clearError();
    setBusy('connect');
    try {
      const payload = await invoke(reconnect ? 'reconnect' : 'start', {
        returnTo: integrationSetupReturnPath('wordpress', setup.returnTo),
        desktopHandoff: isAttuneDesktopShell(),
      });
      const authorizationUrl = pickString(
        payload.authorizationUrl as string,
        payload.authorizeUrl as string,
        payload.url as string
      );
      if (!authorizationUrl) throw new Error('WordPress OAuth did not return an authorization URL');
      window.location.assign(authorizationUrl);
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Unable to start WordPress OAuth');
      setBusy(null);
    }
  };

  const handleRefreshSites = async () => {
    setBusy('sites');
    try {
      const payload = await invoke('sites/refresh');
      const refreshedSites = Array.isArray(payload.sites) ? (payload.sites as WordPressSite[]) : [];
      if (refreshedSites.length) {
        setAvailableSites(refreshedSites);
        setSelectedSiteId(current => current || siteId(refreshedSites[0]));
      }
      await fetchStatus(true);
      toast.success('WordPress sites and permissions refreshed.');
    } catch (error) {
      setup.reportError(
        error instanceof Error ? error.message : 'Unable to refresh WordPress sites'
      );
    } finally {
      setBusy(null);
    }
  };

  const handleBindSite = async () => {
    if (!selectedSiteId) return;
    setBusy('bind');
    try {
      await invoke('site-binding', { siteId: selectedSiteId });
      await fetchStatus(true);
      toast.success('Primary WordPress site updated.');
      clearConnectionDraft();
      setup.onConfigured();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Unable to bind WordPress site');
    } finally {
      setBusy(null);
    }
  };

  const handleRefreshContent = async () => {
    setBusy('content');
    try {
      await invoke('content/refresh');
      await fetchStatus(true);
      toast.success('WordPress page and post mappings refreshed.');
    } catch (error) {
      setup.reportError(
        error instanceof Error ? error.message : 'Unable to refresh WordPress content'
      );
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    setBusy('disconnect');
    try {
      await invoke('disconnect');
      setStatus(EMPTY_STATUS);
      setSelectedSiteId('');
      setAvailableSites([]);
      toast.success('WordPress.com disconnected.');
    } catch (error) {
      setup.reportError(
        error instanceof Error ? error.message : 'Unable to disconnect WordPress.com'
      );
    } finally {
      setBusy(null);
    }
  };

  const connected = status.connected === true;
  const hasConnection = Boolean(status.connection);
  const binding =
    status.binding ||
    status.primarySite ||
    status.primary_site ||
    status.siteBinding ||
    status.site_binding ||
    null;
  const sites = availableSites.length
    ? availableSites
    : Array.isArray(status.sites)
      ? status.sites
      : binding
        ? [binding]
        : [];
  const capabilities = status.capabilities || status.permissions || {};
  const canRead =
    connected &&
    Boolean(
      binding?.canRead ??
        capabilities.readContent ??
        capabilities.read_content ??
        (capabilities as WordPressCapabilitySummary & { read?: boolean }).read
    );
  const providerCanWrite =
    connected &&
    Boolean(
      binding?.canWrite ??
        capabilities.writeContent ??
        capabilities.write_content ??
        (capabilities as WordPressCapabilitySummary & { write?: boolean }).write
    );
  const canWrite = deliveryApprovalsEnabled && providerCanWrite;
  const hasStats =
    connected &&
    Boolean(
      binding?.statsAvailable ??
        status.statsAvailable ??
        status.stats_available ??
        capabilities.statistics ??
        capabilities.stats ??
        (capabilities as WordPressCapabilitySummary & { statistics?: boolean }).statistics
    );
  const disabledReasons = [
    ...(!deliveryApprovalsEnabled && providerCanWrite
      ? ['Delivery Approvals must be enabled before WordPress.com writes are available.']
      : []),
    ...(capabilities.disabledReasons || capabilities.disabled_reasons || []),
    ...(status.connection?.warnings || []),
  ];
  const warning =
    status.warning ||
    status.connection?.warning ||
    loadError ||
    disabledReasons[0] ||
    (status.needsAttention
      ? status.connection?.statusDetail || 'Connection needs attention.'
      : null);
  const effectiveState = !connected
    ? 'Not connected'
    : !binding
      ? 'Select site'
      : canWrite
        ? 'Write ready'
        : canRead
          ? 'Read only'
          : 'Needs attention';
  const tone = !connected ? 'idle' : warning || !binding || !canWrite ? 'warning' : 'connected';
  const siteOptions = useMemo(
    () =>
      sites.map(site => ({
        value: siteId(site),
        label: `${siteTitle(site)}${site.url ? ` — ${site.url}` : ''}`,
        disabled: !siteId(site),
      })),
    [sites]
  );

  if (loading) {
    return (
      <IntegrationCard
        icon={<Globe2 size={18} />}
        title="WordPress.com MCP"
        description="Attune-managed delivery and native WordPress.com site statistics."
        statusLabel="Loading"
        statusTone="idle"
        meta={<Badge colorPalette="blue">Official Attune plugin</Badge>}
      >
        <IntegrationSetupError message={setup.error} />
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Checking connection" />
      </IntegrationCard>
    );
  }

  return (
    <IntegrationCard
      icon={<Globe2 size={18} />}
      title="WordPress.com MCP"
      description="Deliver approved page and post edits and read live, provider-native site statistics. Nothing is installed on your WordPress site."
      statusLabel={effectiveState}
      statusTone={tone}
      meta={<Badge colorPalette="blue">Official Attune plugin</Badge>}
      action={
        hasConnection ? (
          <HStack gap="1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void handleConnect(true)}
              disabled={Boolean(busy)}
            >
              <RefreshCw size={14} />
              Reconnect
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDisconnect}
              disabled={Boolean(busy)}
              color="ui.text.tertiary"
              _hover={{ color: 'status.danger.fg', bg: 'ui.surface.card' }}
            >
              {busy === 'disconnect' ? <InlineSpinner size="xs" /> : <Unlink size={14} />}
              Disconnect
            </Button>
          </HStack>
        ) : (
          <Button
            size="sm"
            bg="accent.solid"
            color="accent.contrast"
            _hover={{ bg: 'accent.emphasized' }}
            onClick={() => void handleConnect(false)}
            disabled={Boolean(busy)}
          >
            {busy === 'connect' ? <InlineSpinner size="xs" /> : <ExternalLink size={14} />}
            Connect WordPress.com
          </Button>
        )
      }
    >
      <IntegrationSetupError message={setup.error} />
      {connected ? (
        <>
          <IntegrationStatusRow
            icon={binding ? <Check size={16} /> : <AlertCircle size={16} />}
            title={binding ? siteTitle(binding) : 'Choose one primary site'}
            description={
              binding?.url ||
              'Only the selected site is available to Attune tools and project members.'
            }
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefreshSites}
                disabled={Boolean(busy)}
              >
                {busy === 'sites' ? <InlineSpinner size="xs" /> : <RefreshCw size={14} />}
                Refresh
              </Button>
            }
          >
            {siteOptions.length ? (
              <HStack mt="2" gap="2" align="center" flexWrap="wrap">
                <Box minW={{ base: '100%', md: '320px' }} flex="1">
                  <AttuneSelect
                    ariaLabel="Primary WordPress site"
                    options={siteOptions}
                    value={selectedSiteId}
                    onValueChange={setSelectedSiteId}
                    size="sm"
                  />
                </Box>
                <Button
                  size="sm"
                  onClick={handleBindSite}
                  disabled={Boolean(busy) || !selectedSiteId || selectedSiteId === siteId(binding)}
                >
                  {busy === 'bind' ? <InlineSpinner size="xs" /> : 'Use this site'}
                </Button>
              </HStack>
            ) : null}
          </IntegrationStatusRow>
          <IntegrationStatusRow
            icon={<ShieldCheck size={16} />}
            title="Effective permissions"
            description={`${canRead ? 'Content read' : 'Content read unavailable'} · ${
              canWrite ? 'approval-gated writes' : 'writes unavailable'
            } · ${hasStats ? 'site statistics' : 'statistics unavailable'}`}
          />
          <IntegrationStatusRow
            icon={<FilePenLine size={16} />}
            title="Pages and posts"
            description="Refresh the permitted WordPress inventory and canonical URL mappings on demand."
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefreshContent}
                disabled={Boolean(busy) || !binding || !canRead}
              >
                {busy === 'content' ? <InlineSpinner size="xs" /> : <RefreshCw size={14} />}
                Refresh content
              </Button>
            }
          />
          <IntegrationStatusRow
            icon={<BarChart3 size={16} />}
            title="Native site statistics"
            description={
              hasStats
                ? 'Available as a distinct live WordPress Stats summary in Analytics.'
                : 'Not available for this site or WordPress.com plan.'
            }
          />
          <IntegrationStatusRow
            icon={<Globe2 size={16} />}
            title="Connection verification"
            description={lastVerifiedLabel(
              status.connection?.verifiedAt ||
                status.connection?.lastVerifiedAt ||
                status.connection?.last_verified_at ||
                binding?.lastVerifiedAt
            )}
          />
          {warning ? (
            <IntegrationNotice icon={<AlertCircle size={16} />} tone="warning">
              {warning}
            </IntegrationNotice>
          ) : null}
        </>
      ) : (
        <VStack align="stretch" gap="0">
          <IntegrationStatusRow
            icon={<ShieldCheck size={16} />}
            title="OAuth 2.1 connection"
            description="Attune uses the fixed WordPress.com MCP endpoint, PKCE, and runtime capability discovery."
          />
          <IntegrationStatusRow
            icon={<FilePenLine size={16} />}
            title="Human-approved delivery"
            description="Draft and live writes remain blocked until a project manager approves the exact preview."
          />
          {warning ? (
            <IntegrationNotice icon={<AlertCircle size={16} />} tone="warning">
              {warning}
            </IntegrationNotice>
          ) : null}
        </VStack>
      )}
    </IntegrationCard>
  );
}
